import { useEffect } from 'react';

import { BrowserRouter as Router, Route, Redirect, Switch } from 'react-router-dom';
import NavigationBar from './NavigationBar';
import Search from './Search';
import Biblio from './Biblio';
import Sort from './Sort';
import Flags from './Flags';
import Files from './Files';
import Mining from './Mining';
import Ontomate from './Ontomate';
import Textpresso from './Textpresso';
import Create from './Create';
import Merge from './Merge';
import Reports from './Reports';
import Download from './Download';
import BulkSubmission from './BulkSubmission';
import About from './About';
import Tracker from './Tracker';
import Resources from './Resources';
import Laboratory from './Laboratory';
import Person from './Person';
import LoginPage from './LoginPage';
import ReAuthModal from './ReAuthModal';
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { setMods } from '../actions/appActions';
import { DebeziumStatusAlert } from './DebeziumStatusAlert';
import { useTokenSync } from '../hooks/useTokenSync';
import { useVersionCheck } from '../hooks/useVersionCheck';
import VersionUpdateBanner from './VersionUpdateBanner';
import { useSystemBanner } from '../hooks/useSystemBanner';
import SystemBanner from './SystemBanner';

import { api } from '../api';

// AWS Amplify imports
import { Amplify } from 'aws-amplify';
import { cognitoConfig } from '../config';

// Configure Amplify
Amplify.configure(cognitoConfig);

// No secrets here so print out for sanity checking.
console.log("UI_URL -> " + process.env.REACT_APP_UI_URL);
console.log("UI_RESTAPI-> " + process.env.REACT_APP_RESTAPI);
console.log("UI_SW -> " + process.env.REACT_APP_SWAGGERUI);
console.log("UI_DSP -> " + process.env.REACT_APP_DEV_OR_STAGE_OR_PROD);
console.log(Router);

// PrivateRoute component for protected routes - redirects to login if not authenticated.
// observerBlocked: curation pages an observer must not reach even via a
// hand-typed URL or stale bookmark — matches the links hidden from the navbar;
// the API rejects observer mutations independently (SCRUM-6431).
const PrivateRoute = ({ component: Component, observerBlocked = false, ...rest }) => {
    const isSignedIn = useSelector(state => state.isLogged.isSignedIn);
    const cognitoObserver = useSelector(state => state.isLogged.cognitoObserver);
    return (
        <Route
            {...rest}
            render={(props) =>
                isSignedIn ? (
                    (observerBlocked && cognitoObserver) ? (
                        <Redirect to={{ pathname: '/search' }} />
                    ) : (
                        <Component {...props} />
                    )
                ) : (
                    <Redirect to={{
                        pathname: '/login',
                        state: { from: props.location }
                    }} />
                )
            }
        />
    );
};

const AppWithRouterAccess = () => {
    const dispatch = useDispatch();

    // Initialize cross-tab token synchronization
    useTokenSync();

    // Check for new application versions on tab visibility change
    const { updateAvailable } = useVersionCheck();

    // Externally-controlled system-status banner (maintenance / slowness, etc.)
    const systemBanner = useSystemBanner();

    const devOrStageOrProd = process.env.REACT_APP_DEV_OR_STAGE_OR_PROD;
    const isSignedIn = useSelector(state => state.isLogged.isSignedIn);

    let documentTitle = 'Literature AGRKB';
    if (devOrStageOrProd === 'prod') { }
      else if (devOrStageOrProd === 'stage') { documentTitle = 'Stage-' + documentTitle; }
      else { documentTitle = devOrStageOrProd + '-' + documentTitle; }

    useEffect(() => {
        document.title = documentTitle;
    }, [documentTitle]);

    // Fetch MOD taxons only when signed in (API requires auth)
    useEffect(() => {
      if (!isSignedIn) return;

      const fetchTaxons = async () => {
        try {
          const taxons = await api.get("/mod/taxons/default");
          const sortedMods = taxons.data.map(taxon => taxon.mod_abbreviation).sort();
          dispatch(setMods(sortedMods));
        } catch (error) {
          console.error('Error fetching taxons:', error);
        }
      }
      fetchTaxons();
    }, [dispatch, isSignedIn]);

    const cognitoMod = useSelector(state => state.isLogged.cognitoMod);
    const cognitoTester = useSelector(state => state.isLogged.cognitoTester);
    const testerMod = useSelector(state => state.isLogged.testerMod);
    const classTesterString = (devOrStageOrProd === 'prod') ? 'App App-testing-prod' : 'App App-testing-dev';
    const className = (cognitoMod !== testerMod && testerMod !== 'No' && cognitoTester) ? classTesterString : 'App';

    return (
        <div className={className}>
            {systemBanner && <SystemBanner message={systemBanner.message} severity={systemBanner.severity} />}
            {updateAvailable && <VersionUpdateBanner />}
            <ReAuthModal />
            <Switch>
                <Route path='/login' component={LoginPage} />
                <Route>
                    <NavigationBar />
                    <DebeziumStatusAlert />
                    <br />
                    <PrivateRoute path='/' exact={true} component={Search}/>
                    <PrivateRoute path='/search' component={Search} />
                    <PrivateRoute path='/biblio' component={Biblio} />
                    <PrivateRoute path='/sort' observerBlocked component={Sort} />
                    <PrivateRoute path='/flags' component={Flags} />
                    <PrivateRoute path='/files' component={Files} />
                    <PrivateRoute path='/mining' component={Mining} />
                    <PrivateRoute path='/ontomate' component={Ontomate} />
                    <PrivateRoute path='/textpresso' component={Textpresso} />
                    <PrivateRoute path='/create' observerBlocked component={Create} />
                    <PrivateRoute path='/merge' observerBlocked component={Merge} />
                    <PrivateRoute path='/reports' component={Reports} />
                    <PrivateRoute path='/download' component={Download} />
                    <PrivateRoute path='/bulkSubmission' observerBlocked component={BulkSubmission} />
                    <PrivateRoute path='/about' component={About} />
                    <PrivateRoute path='/tracker' observerBlocked component={Tracker} />
                    <PrivateRoute path='/resources' component={Resources} />
                    <PrivateRoute path='/lab' observerBlocked component={Laboratory} />
                    <PrivateRoute path='/person' observerBlocked component={Person} />
                </Route>
            </Switch>
        </div>
    );
};

export default AppWithRouterAccess;
